import { Link } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { useStoreInfo } from '../../lib/appSettings';
import { t } from '../../i18n/pt';

/**
 * Terms and conditions.
 *
 * Doubles as the seller's legal information page (the footer's "Informação
 * Legal" and "Condições de Venda" both land here), so section 1 carries the
 * identification Portuguese e-commerce law requires. Those values come from the
 * API rather than being typed in, so the page, the footer and the invoice PDFs
 * always agree.
 */
export default function TermsPage(): JSX.Element {
  const storeInfo = useStoreInfo();
  const contactEmail = storeInfo.email || t.footer.contactEmail;

  return (
    <>
      <SEO title="Termos e Condições" />
      <div className="container" style={{ maxWidth: 800 }}>
        <h1>Termos e Condições</h1>
        <p>
          Ao utilizares o site Rosita Store, aceitas os termos e condições abaixo. Se não
          concordares com eles, por favor não utilizes este site.
        </p>

        <h2 id="identificacao">1. Identificação do vendedor</h2>
        <ul>
          <li>
            <strong>Nome comercial:</strong> {storeInfo.name}
          </li>
          {storeInfo.legalName ? (
            <li>
              <strong>Titular:</strong> {storeInfo.legalName}
            </li>
          ) : null}
          {storeInfo.nif ? (
            <li>
              <strong>NIF:</strong> {storeInfo.nif}
            </li>
          ) : null}
          {storeInfo.address ? (
            <li>
              <strong>Sede:</strong> {storeInfo.address}
            </li>
          ) : null}
          {storeInfo.phone ? (
            <li>
              <strong>Telefone:</strong> {storeInfo.phone}
            </li>
          ) : null}
          <li>
            <strong>E-mail:</strong> <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </li>
        </ul>

        <h2>2. Encomendas e preços</h2>
        <p>
          Todas as encomendas estão sujeitas a disponibilidade de stock e a confirmação do
          pagamento. Os preços apresentados são em euros e <strong>incluem IVA</strong> à taxa
          legal em vigor. Os custos de envio são apresentados em separado e antes de finalizares a
          compra — a tabela completa está em{' '}
          <Link to="/entregas-e-devolucoes">Entregas e Devoluções</Link>.
        </p>
        <p>
          O contrato de compra e venda considera-se celebrado quando recebes o e-mail de
          confirmação da encomenda. Se uma peça deixar de estar disponível depois disso,
          contactamos-te e devolvemos o valor correspondente.
        </p>

        <h2>3. Pagamento</h2>
        <p>
          Os pagamentos são processados pela EasyPay, entidade autorizada pelo Banco de Portugal.
          Aceitamos <strong>MB WAY</strong>, <strong>referência Multibanco</strong> e{' '}
          <strong>cartão de crédito ou débito</strong>. Os dados do teu cartão são introduzidos
          diretamente no ambiente seguro do processador de pagamentos — a Rosita Store não os
          recebe nem os guarda.
        </p>
        <p>
          Uma referência Multibanco só confirma a encomenda depois de paga, dentro do prazo de
          validade indicado. Passado esse prazo sem pagamento, a encomenda é cancelada e o stock
          libertado.
        </p>

        <h2>4. Entregas</h2>
        <p>
          Entregamos em todo o território nacional (Portugal Continental, Açores e Madeira) através
          de parceiros logísticos, e também podes optar por recolha num ponto de entrega quando
          existir para a tua zona. Os custos e os prazos estimados estão em{' '}
          <Link to="/entregas-e-devolucoes">Entregas e Devoluções</Link>. Os prazos contam-se em dias
          úteis a partir da confirmação do pagamento e são estimativas do transportador.
        </p>

        <h2 id="devolucoes">5. Direito de livre resolução (devoluções)</h2>
        <p>
          Tens <strong>14 dias</strong> a contar da data em que recebes a encomenda para resolver o
          contrato, sem necessidade de indicar qualquer motivo, nos termos do Decreto-Lei n.º
          24/2014. Para exercer esse direito, basta escreveres-me para{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a> dentro do prazo, indicando o número
          da encomenda e as peças que queres devolver.
        </p>
        <p>
          Depois de comunicares a decisão, tens mais 14 dias para devolver as peças. Estas devem
          vir sem sinais de uso, com as etiquetas originais.
        </p>
        <p>
          Os <strong>custos do envio de retorno são suportados por ti</strong>, salvo se a peça
          apresentar defeito ou não corresponder à encomendada, casos em que são suportados por
          nós. O reembolso é feito no prazo de <strong>14 dias</strong> a contar da receção da
          devolução, pelo mesmo meio de pagamento utilizado na compra.
        </p>
        <p>
          O procedimento detalhado está em{' '}
          <Link to="/entregas-e-devolucoes#devolucoes">Entregas e Devoluções</Link>.
        </p>

        <h2>6. Garantia e falta de conformidade</h2>
        <p>
          As peças beneficiam da <strong>garantia legal de 3 anos</strong> prevista no Decreto-Lei
          n.º 84/2021. Em caso de falta de conformidade, tens direito à reposição da conformidade
          por reparação ou substituição, à redução proporcional do preço ou à resolução do
          contrato. Comunica a falta de conformidade para{' '}
          <a href={`mailto:${contactEmail}`}>{contactEmail}</a>, com fotografias e o número da
          encomenda.
        </p>

        <h2 id="litigios">7. Reclamações e resolução de litígios</h2>
        <p>
          Podes apresentar uma reclamação através do{' '}
          <a href="https://www.livroreclamacoes.pt/" target="_blank" rel="noreferrer">
            Livro de Reclamações eletrónico
          </a>
          .
        </p>
        <p>
          Em caso de litígio de consumo, podes recorrer a uma entidade de{' '}
          <strong>resolução alternativa de litígios (RAL)</strong>. A entidade competente para a
          sede da Rosita Store é o{' '}
          <a
            href="https://www.centrodearbitragemdecoimbra.com/"
            target="_blank"
            rel="noreferrer"
          >
            Centro de Arbitragem de Conflitos de Consumo do Distrito de Coimbra
          </a>{' '}
          (Avenida Fernão de Magalhães, n.º 240, 1.º, 3000-172 Coimbra), cuja competência abrange
          o concelho da Figueira da Foz.
        </p>
        <p>
          Em alternativa, o{' '}
          <a href="https://www.cniacc.pt/" target="_blank" rel="noreferrer">
            CNIACC — Centro Nacional de Informação e Arbitragem de Conflitos de Consumo
          </a>{' '}
          tem competência supletiva a nível nacional. A lista completa de entidades pode ser
          consultada no{' '}
          <a href="https://www.consumidor.gov.pt/" target="_blank" rel="noreferrer">
            Portal do Consumidor
          </a>
          .
        </p>

        <h2>8. Propriedade intelectual</h2>
        <p>
          Os textos, fotografias, marcas e restantes conteúdos deste site pertencem à Rosita Store
          e não podem ser reproduzidos sem autorização prévia.
        </p>

        <h2>9. Dados pessoais</h2>
        <p>
          O tratamento dos teus dados pessoais está descrito na{' '}
          <Link to="/politica-de-privacidade">Política de Privacidade</Link>.
        </p>

        <h2>10. Lei aplicável</h2>
        <p>
          Aplica-se a lei portuguesa. Em caso de litígio, é competente o tribunal da comarca do
          domicílio do consumidor, sem prejuízo do recurso às entidades de resolução alternativa de
          litígios indicadas no ponto 7.
        </p>
      </div>
    </>
  );
}
